
'use client';

import type { Trip, SpeedCheckResult } from '@/lib/types';
import { Logo } from './icons';
import { Timer, Waypoints, Gauge, Zap, Flag, ParkingCircle, Pin, TimerOff, Milestone } from 'lucide-react';

const formatDuration = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return { value: '--', unit: undefined };
    const totalSeconds = Math.round(seconds);
    if (totalSeconds < 60) {
        return { value: totalSeconds.toString(), unit: 's' };
    }
    if (totalSeconds < 3600) {
        return { value: (totalSeconds / 60).toFixed(1), unit: 'min' };
    }
    const hours = totalSeconds / 3600;
    return { value: hours.toFixed(2), unit: 'hr' };
};

const ShareStat = ({ icon: Icon, title, value, unit, description, className }: { icon: React.ElementType, title: string, value: string, unit?: string, description?: string, className?: string }) => (
    <div className={`flex items-center gap-3 ${className}`}>
        <div className={`p-2 rounded-full ${className ? '' : 'bg-primary/10 text-primary'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-1">
                <p className="text-xl font-bold">{value}</p>
                {unit && <p className={`text-sm ${className ? '' : 'text-muted-foreground'}`}>{unit}</p>}
            </div>
            {description && <p className="text-xs text-muted-foreground -mt-1">{description}</p>}
        </div>
    </div>
);

type TripShareCardProps = {
  trip: Trip;
};

export function TripShareCard({ trip }: TripShareCardProps) {
  const durationDisplay = formatDuration(trip.duration);
  const totalStopTimeDisplay = formatDuration(trip.totalStopTime);
  const timeTo100Display = formatDuration(trip.timeTo100);
  const oneKmResultTimeDisplay = formatDuration(trip.oneKmResult?.time);
  const distanceDisplayValue = trip.distance < 1 ? trip.distance.toFixed(2) : trip.distance.toFixed(1);

  const allPoints = trip.significantEvents || trip.tripData || [];
  const firstPoint = allPoints.length > 0 ? allPoints[0] : null;
  const lastPoint = allPoints.length > 0 ? allPoints[allPoints.length - 1] : null;

  return (
    <div className="w-[450px] bg-card text-card-foreground p-6 font-sans border-2 border-primary rounded-xl shadow-2xl">
      <div className="flex justify-between items-start mb-4">
        <div>
            <h2 className="text-2xl font-bold">Trip Summary</h2>
            <p className="text-sm text-muted-foreground">{new Date(trip.date).toLocaleString()}</p>
        </div>
        <Logo className="w-8 h-8 text-primary" />
      </div>
      
      {(firstPoint || lastPoint) && (
        <div className="mb-4 space-y-1 text-xs text-muted-foreground p-3 rounded-lg bg-background border">
            {firstPoint && (
              <div className="flex items-center">
                <Pin className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                <span className="font-semibold">Start:</span>&nbsp;
                <span>{new Date(firstPoint.timestamp).toLocaleTimeString()} ({firstPoint.lat?.toFixed(3)}, {firstPoint.lon?.toFixed(3)})</span>
              </div>
            )}
            {lastPoint && firstPoint?.timestamp !== lastPoint.timestamp && (
              <div className="flex items-center">
                <Flag className="w-3.5 h-3.5 mr-2 flex-shrink-0" />
                <span className="font-semibold">End:</span>&nbsp;&nbsp;&nbsp;
                <span>{new Date(lastPoint.timestamp).toLocaleTimeString()} ({lastPoint.lat?.toFixed(3)}, {lastPoint.lon?.toFixed(3)})</span>
              </div>
            )}
        </div>
      )}

      {trip.speedCheckResults && trip.speedCheckResults.length > 0 && (
          <div className="mb-4 space-y-2">
              {trip.speedCheckResults.map(check => {
                  const avgSpeed = check.duration > 0 ? (check.distance / (check.duration / 3600)) : 0;
                  return (
                      <div key={check.id} className="flex items-center p-2 rounded-md border border-dashed border-primary/50 bg-primary/10">
                          <Milestone className="w-4 h-4 mr-3 flex-shrink-0 text-primary" />
                          <div>
                              <p className="font-semibold text-primary text-sm">Avg. Speed Check</p>
                              <p className="text-primary/90 text-xs">{avgSpeed.toFixed(1)} km/h over {check.distance.toFixed(2)} km</p>
                          </div>
                      </div>
                  )
                })}
          </div>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <ShareStat icon={Timer} title="Duration" value={durationDisplay.value} unit={durationDisplay.unit} />
        <ShareStat icon={Waypoints} title="Distance" value={distanceDisplayValue} unit="km" />
        <ShareStat icon={Gauge} title="Avg Speed" value={trip.avgSpeed.toFixed(0)} unit="km/h" className="text-yellow-400 bg-yellow-400/10" />
        <ShareStat icon={Gauge} title="Max Speed" value={trip.maxSpeed.toFixed(0)} unit="km/h" className="text-red-500 bg-red-500/10" />
        <ShareStat icon={ParkingCircle} title="Stops" value={(trip.stopCount ?? 0).toString()} />
        <ShareStat icon={TimerOff} title="Time Stopped" value={totalStopTimeDisplay.value} unit={totalStopTimeDisplay.unit} />
        <ShareStat icon={Zap} title="0-100 km/h" value={timeTo100Display.value} unit={timeTo100Display.unit} />
        <ShareStat icon={Flag} title="1km Time" value={oneKmResultTimeDisplay.value} unit={oneKmResultTimeDisplay.unit} description={trip.oneKmResult ? `Max: ${trip.oneKmResult.maxSpeed.toFixed(0)} km/h` : ' '} />
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-xs text-muted-foreground">Shared from</p>
        <p className="font-bold text-primary">Velocity Onsights</p>
      </div>
    </div>
  );
}
